/**
 * Per voice-loop iter 7 — module-level cache for pre-warmed TTS
 * synth blobs. Used by the greeting pre-warm path: as soon as the
 * character's `info.voice` + `info.greeting.text` is known we kick
 * off the synth in the background. By the time the controller is
 * actually ready to play the greeting (3D model finished loading,
 * attachAudio wired), the blob is already in this cache and we
 * skip re-synthing.
 *
 * One-shot semantics: `consume*` removes the entry. We never want
 * the same blob played twice across turns.
 *
 * Module-level rather than React-context because:
 *   - Greeting synth + speak run in the same EmbedRuntime instance
 *   - Easier to thread through speak.ts which is plain JS
 *   - The cache is bounded (only greeting text ever lands here)
 */

interface CacheEntry {
  blob: Blob;
  cachedAt: number;
}

const cache = new Map<string, CacheEntry>();

/** Cache TTL — abandoned entries from a refresh / mount-unmount
 *  pair shouldn't leak. 30s is plenty for the greeting flow which
 *  fires within ~2s of mount. */
const TTL_MS = 30_000;

function cacheKey(characterId: string, text: string): string {
  // Length + prefix is enough to dedup the realistic cases (greeting
  // text is small, doesn't collide). Avoids hashing the full string
  // on every lookup.
  return `${characterId}::${text.length}::${text.slice(0, 80)}`;
}

export function stashSynth(characterId: string, text: string, blob: Blob): void {
  cache.set(cacheKey(characterId, text), { blob, cachedAt: Date.now() });
}

/** Returns the cached blob and REMOVES the entry. Returns null if
 *  no cache hit or the entry is past its TTL. */
export function consumeSynth(characterId: string, text: string): Blob | null {
  const k = cacheKey(characterId, text);
  const hit = cache.get(k);
  if (!hit) return null;
  cache.delete(k);
  if (Date.now() - hit.cachedAt > TTL_MS) return null;
  return hit.blob;
}

/** Drop everything — useful if we ever want a wipe path. Unused
 *  currently; provided for tests + future iters. */
export function clearSynthCache(): void {
  cache.clear();
}
